import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { api, ApiError } from '../api.js';
import GoogleButton from '../components/GoogleButton.js';
import LanguageToggle from '../components/LanguageToggle.js';
import { authClient, useSession } from '../auth-client.js';
import logoUrl from '../assets/logo.png';
import styles from './JoinScreen.module.css';

/**
 * Opening an "anyone with the link" invitation.
 *
 * The link identifies a group; it is never a credential. Someone arriving
 * without an account sees what they are being invited to first, then chooses
 * how to get in — in that order, because being asked to authenticate before
 * you know what for is exactly the friction that makes people abandon.
 *
 * Two ways in, offered side by side (PLAN.md Phase 10.29):
 *
 *   sign in    Google. Full run of the app — many circles, invitations, your
 *              own circles — and the same account on any device.
 *
 *   no account A name and a 6-digit PIN, for this circle only
 *              (`GuestGateScreen`). No email, no password, nothing to verify.
 *              The point is the relative who will not register for a family
 *              bolão, and who otherwise simply does not play.
 *
 * Neither is a dead end: someone who starts without an account can register
 * later and keep everything (`UpgradeScreen`).
 *
 * Google returns to this same URL, so after signing in the visitor lands back
 * here with the preview already resolved and only the join step left.
 */
export default function JoinScreen() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: session, isPending: sessionPending } = useSession();
  const [displayName, setDisplayName] = useState('');

  const {
    data: preview,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['join-preview', token, session?.user?.id ?? null],
    queryFn: () => api.previewJoin(token),
    retry: false,
  });

  // Default to the account's own name; most people will keep it.
  useEffect(() => {
    if (session?.user?.name && !displayName) setDisplayName(session.user.name);
  }, [session?.user?.name, displayName]);

  const join = useMutation({
    mutationFn: () => api.joinViaLink(token, displayName.trim()),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
      void navigate(`/groups/${result.groupId}`, { replace: true });
    },
  });

  if (sessionPending || isPending) {
    return (
      <main className={styles.screen}>
        <p className="muted">{t('common.loading')}</p>
      </main>
    );
  }

  if (isError || !preview) {
    return (
      <main className={styles.screen}>
        <div className={`panel ${styles.card}`}>
          <h1>{t('join.invalid')}</h1>
          <p className="muted">{t('join.invalidHint')}</p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.screen}>
      <div className={styles.topBar}>
        <LanguageToggle />
      </div>

      <div className={styles.brand}>
        <img className={styles.logo} src={logoUrl} alt="" />
      </div>

      <div className={`panel ${styles.card}`}>
        <div>
          <h1 className={styles.title}>{t('join.title', { group: preview.groupName })}</h1>
          <p className="muted">{t('join.invitedBy', { name: preview.organizerName })}</p>
          <p className="muted">
            {t('groups.playerCount', { count: preview.memberCount })} ·{' '}
            {t('pool.poolCount', { count: preview.poolCount })}
          </p>
        </div>

        {preview.alreadyMember ? (
          <>
            <p>{t('join.alreadyMember')}</p>
            <button
              type="button"
              className="button button-primary"
              onClick={() => void navigate(`/groups/${preview.groupId}`)}
            >
              {t('groups.open')}
            </button>
          </>
        ) : session?.user ? (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              if (displayName.trim() && !join.isPending) join.mutate();
            }}
          >
            <div>
              <label className="label" htmlFor="displayName">
                {t('join.nameLabel')}
              </label>
              <input
                id="displayName"
                className="input"
                value={displayName}
                maxLength={60}
                placeholder={t('join.namePlaceholder')}
                onChange={(event) => setDisplayName(event.target.value)}
              />
              <p className="muted" style={{ fontSize: '0.8rem', marginTop: 'var(--space-1)' }}>
                {t('join.nameHint')}
              </p>
            </div>

            {join.isError ? (
              <p className="error">
                {join.error instanceof ApiError && join.error.code === 'name_taken'
                  ? t('join.nameTaken')
                  : t('common.somethingWentWrong')}
              </p>
            ) : null}

            <button
              type="submit"
              className="button button-primary"
              disabled={!displayName.trim() || join.isPending}
            >
              {join.isPending ? t('join.joining') : t('join.submit')}
            </button>
          </form>
        ) : (
          <>
            {/* Both doors, in the order most people want them. Returning to
                this same URL means the invitation survives the round trip to
                Google. */}
            <p className="muted">{t('join.chooseHow')}</p>
            <GoogleButton
              label={t('auth.signInWithGoogle')}
              onClick={() =>
                void authClient.signIn.social({
                  provider: 'google',
                  callbackURL: window.location.href,
                })
              }
            />

            <div className={styles.divider}>{t('auth.or')}</div>

            <Link
              className="button button-secondary"
              style={{ display: 'block', textAlign: 'center' }}
              to={`/join/${encodeURIComponent(token)}/players`}
            >
              {t('join.withoutAccount')}
            </Link>
            <p className="muted" style={{ fontSize: '0.8rem', textAlign: 'center' }}>
              {t('join.withoutAccountHint')}
            </p>
          </>
        )}
      </div>
    </main>
  );
}
